import config from "@/constants/Config";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import { getOrientationStateInfo } from "@/utils/orientationStateUtils";
import {
  MEASUREMENTS,
  MappedStateDto,
  ORIENTATION_SIDES,
  SensorRangeOperator,
  StateMappingDto,
  StateMappingScopeDto,
  StateMappingScopeType,
  StateMappingType,
} from "@apis/stateMappingClient/stateMappingClient.dto";

/**
 * STIC-16316: BLE sensor mappings are not released yet: their tab, the mapping a slot on the organization
 * default uses and the profile hint stay hidden, and the slot's options keep their former names. true
 * brings them back, together with state_mapping.sensor.enabled on the server.
 */
export const SENSOR_STATE_MAPPINGS_ENABLED = false;

const SYSTEM_IMAGE_PATH = "/rest/image_library/system/";
const SIDES_WITH_SYSTEM_IMAGE = new Set<string>(ORIENTATION_SIDES);

/** the boards' zebra rows: the first, third, … row of the body */
const ZEBRA_ROW = { backgroundColor: "var(--sensolus-Sensolus_BG_Zebra)" };

export const zebraRow = (index: number) =>
  index % 2 === 0 ? ZEBRA_ROW : undefined;

/** the symbols of the tests that compare a sensor value with one number */
export const COMPARISON_SYMBOL: Record<
  Exclude<SensorRangeOperator, "BETWEEN">,
  string
> = {
  EQ: "=",
  LT: "<",
  LE: "≤",
  GT: ">",
  GE: "≥",
};

/** the platform's own names of the product-sheet measurements, without their unit */
const MEASUREMENT_LABEL: Record<string, LanguageKey> = {
  temperature: LanguageKey.temperature_key,
  humidity: LanguageKey.ble_humidity_key,
  distancecm: LanguageKey.distance_key,
  eventcount: LanguageKey.ble_eventcount_key,
  switchState: LanguageKey.switchstate_key,
  presence: LanguageKey.ble_presence_key,
  orientation: LanguageKey.orientation_unit_key,
  activeTime: LanguageKey.activetime_key,
  voltage: LanguageKey.metric_type_graph_voltage_title_key,
  amplitude: LanguageKey.amplitude_key,
  rotation: LanguageKey.rotation_key,
  pressure: LanguageKey.flight_log_pressure_key,
  ampHour: LanguageKey.amp_hours_key,
};

export const measurementLabel = (measurement?: string | null): string =>
  measurement
    ? MEASUREMENT_LABEL[measurement]
      ? translate(MEASUREMENT_LABEL[measurement])
      : measurement
    : "";

export const measurementUnit = (measurement?: string | null): string =>
  MEASUREMENTS.find((m) => m.value === measurement)?.unit || "";

/** "Temperature (°C)", or just the name when the measurement has no unit */
export const measurementWithUnit = (measurement?: string | null): string => {
  const unit = measurementUnit(measurement);
  const label = measurementLabel(measurement);
  return unit ? `${label} (${unit})` : label;
};

/** absolute url of a state's icon: its image library path, else the system icon; size "small" for lists */
export const stateIconUrl = (
  image: string | null | undefined,
  key: string,
  type: StateMappingType = "ORIENTATION",
  size?: "small"
): string => {
  const query = (params: string[]) =>
    params.length ? `?${params.join("&")}` : "";
  if (image)
    return config.API_URL + image + query(size ? [`size=${size}`] : []);
  const name =
    type === "SENSOR" || !SIDES_WITH_SYSTEM_IMAGE.has(key) ? "unknown" : key;
  const params = [
    ...(type === "SENSOR" ? ["type=SENSOR"] : []),
    ...(size ? [`size=${size}`] : []),
  ];
  return config.API_URL + SYSTEM_IMAGE_PATH + name + query(params);
};

/** the names a scope lists, without the kind of selection */
export const scopeNames = (scope: StateMappingScopeDto): string => {
  switch (scope.type) {
    case "ASSET_TYPES":
      return scope.assetTypes.map((t) => t.name ?? t.id).join(", ");
    case "TAGS":
      return scope.tags.map((t) => t.name ?? t.id).join(", ");
    case "TRACKER_TYPES":
      return scope.trackerTypes.join(", ");
    default:
      return translate(LanguageKey.all_key);
  }
};

/** what each kind of scope selects, in the names the tracker list uses */
const SCOPE_KIND: Partial<Record<StateMappingScopeType, LanguageKey>> = {
  ASSET_TYPES: LanguageKey.asset_type_key,
  TAGS: LanguageKey.tag_key,
  TRACKER_TYPES: LanguageKey.product_key,
};

/** the "Applies to" cell: what the scope selects, then the names */
export const describeScope = (
  scope: StateMappingScopeDto
): { names: string; kind: string } => {
  const kind = SCOPE_KIND[scope.type];
  return { names: scopeNames(scope), kind: kind ? translate(kind) : "" };
};

const scopeIds = (scope: StateMappingScopeDto): string[] => {
  switch (scope.type) {
    case "ASSET_TYPES":
      return scope.assetTypes.map((t) => String(t.id));
    case "TAGS":
      return scope.tags.map((t) => String(t.id));
    case "TRACKER_TYPES":
      return scope.trackerTypes.map((t) => t.toLowerCase());
    default:
      return [];
  }
};

/**
 * The earlier mapping that already covers everything this one covers (same
 * measurement for sensor mappings), so this one never applies; null otherwise.
 */
export const overlapsWith = (
  mappings: StateMappingDto[],
  index: number
): StateMappingDto | null => {
  const me = mappings[index];
  for (let i = 0; i < index; i++) {
    const other = mappings[i];
    if (me.type === "SENSOR" && other.measurement !== me.measurement) continue;
    if (other.scope.type === "ALL") return other;
    if (other.scope.type === me.scope.type) {
      const mine = scopeIds(me.scope);
      const theirs = new Set(scopeIds(other.scope));
      if (mine.length > 0 && mine.every((id) => theirs.has(id))) return other;
    }
  }
  return null;
};

export const emptyMapping = (type: StateMappingType): StateMappingDto => ({
  type,
  name: "",
  description: "",
  scope: {
    type: "ALL",
    assetTypes: [],
    tags: [],
    trackerTypes: [],
  },
  mode: "FORM",
  stateBySide: {},
  ranges: [],
  otherState: null,
  script: "",
  states: [],
  measurement: type === "SENSOR" ? "temperature" : null,
});

/** one checkbox filter of the asset list (Asset trackers), selecting those values */
const listFilter = (
  categoryId: string,
  filterKey: string,
  selectedValues: (string | number)[]
) => ({
  categoryId,
  filterKey,
  filterType: "MULTI_SELECT_CHECKBOX",
  searchType: "STATIC",
  notFilter: false,
  filterValue: { includeEmpty: false, includeNoEmpty: false, selectedValues },
});

/**
 * the asset list's advanced filters for the trackers a scope selects: asset
 * types (child types included, as the scope), tags or products; a sensor
 * mapping only concerns trackers with the BLE sensor service
 */
export const scopeListFilters = (
  scope: StateMappingScopeDto,
  type: StateMappingType
): unknown[] => {
  const filters: unknown[] = [];
  switch (scope.type) {
    case "ASSET_TYPES":
      filters.push(
        listFilter(
          "assetInfo",
          "assetTypes",
          scope.assetTypes.map((t) => t.id)
        )
      );
      break;
    case "TAGS":
      filters.push(
        listFilter(
          "assetInfo",
          "assetTags",
          scope.tags.map((t) => t.id)
        )
      );
      break;
    case "TRACKER_TYPES":
      filters.push(
        listFilter("trackerType", "productKeyNonTech", scope.trackerTypes)
      );
      break;
    default:
      break;
  }
  if (type === "SENSOR") {
    filters.push(listFilter("trackerConfig", "activeServices", ["BLE_SENSOR"]));
  }
  return filters;
};

/** whether the scope selects anything: all assets, or at least one item of its kind */
export const scopeHasSelection = (scope: StateMappingScopeDto): boolean =>
  scope.type === "ALL" || scopeIds(scope).length > 0;

/**
 * the mapping's states, then the states a form gives without declaring them
 * (a script switched to the form): labelled with their built-in name, else
 * with their key
 */
export const withStatesOf = (
  form: Pick<StateMappingDto, "stateBySide" | "ranges" | "otherState">,
  states: MappedStateDto[],
  type: StateMappingType
): MappedStateDto[] => {
  const declared = new Set(states.map((s) => s.key));
  const added: MappedStateDto[] = [];
  [
    ...Object.values(form.stateBySide || {}),
    ...(form.ranges || []).map((range) => range.state),
    form.otherState || "",
  ].forEach((key) => {
    if (!key || declared.has(key)) return;
    declared.add(key);
    added.push({
      key,
      label: type === "ORIENTATION" ? getOrientationStateInfo(key).name : key,
      image: null,
    });
  });
  return [...states, ...added];
};

/** what the user sees for a state: its label in the user's language, else its key */
export { stateLabel } from "@/utils/stateLabelUtils";

export const fillPlaceholders = (
  text: string,
  ...values: (string | number)[]
): string =>
  values.reduce<string>(
    (result, value, i) =>
      result.replace(new RegExp(`\\{${i}\\}`, "g"), String(value)),
    text
  );

/** a number the way the ranges show it: no trailing zeros */
export const formatBound = (value: number | null | undefined): string =>
  value === null || value === undefined ? "" : String(value);
